import React, { useState, useEffect, useCallback } from 'react';
import { leaveAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Check, X } from 'lucide-react';

const STATUS_BADGE = {
  pending:  'bg-amber-50 text-amber-700',
  approved: 'bg-teal-50 text-teal-700',
  rejected: 'bg-red-50 text-red-600',
};

export default function LeaveTab() {
  const { user, canManage } = useAuth();
  const [types, setTypes]     = useState([]);
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [addingType, setAddingType] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '', annual_days: 14, paid: true, active: true });
  const [reqForm, setReqForm]   = useState({ leave_type_id: '', start_date: '', end_date: '', days: 1, note: '' });
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, r, b] = await Promise.all([
        leaveAPI.getLeaveTypes(),
        leaveAPI.getLeaveRequests(),
        leaveAPI.getMyLeaveBalances(),
      ]);
      setTypes(t.data?.leave_types || []);
      setRequests(r.data?.requests || []);
      setBalances(b.data?.balances || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAddType = async () => {
    if (!typeForm.name) return;
    setSaving(true);
    try {
      await leaveAPI.createLeaveType(typeForm);
      toast.success('Leave type added');
      setTypeForm({ name: '', annual_days: 14, paid: true, active: true });
      setAddingType(false);
      load();
    } catch (e) { toast.error('Failed to add leave type'); }
    finally { setSaving(false); }
  };

  const handleDeleteType = async (id) => {
    try { await leaveAPI.deleteLeaveType(id); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleRequest = async () => {
    if (!reqForm.leave_type_id || !reqForm.start_date || !reqForm.end_date) {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      await leaveAPI.createLeaveRequest(reqForm);
      toast.success('Leave request submitted');
      setShowRequest(false);
      setReqForm({ leave_type_id: '', start_date: '', end_date: '', days: 1, note: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSaving(false); }
  };

  const handleApprove = async (id) => {
    try { await leaveAPI.approveLeaveRequest(id); toast.success('Approved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReject = async (id) => {
    try { await leaveAPI.rejectLeaveRequest(id); toast.success('Rejected'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="leave-tab">

      {/* My Balances */}
      {balances.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">My Leave Balances</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {balances.map(b => (
              <div key={b.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-center">
                <p className="text-xs text-slate-400">{b.leave_type_name}</p>
                <p className="text-xl font-bold text-slate-800">{b.remaining_days}</p>
                <p className="text-xs text-slate-400">of {b.annual_days} days remaining</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leave Types (owner only) */}
      {user?.role === 'owner' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold text-slate-800">Leave Types</p>
            <button onClick={() => setAddingType(a => !a)} data-testid="add-leave-type-btn"
              className="flex items-center gap-1 text-xs font-medium text-orange-500 hover:text-orange-600">
              <Plus size={13} /> Add Type
            </button>
          </div>
          {addingType && (
            <div className="border border-slate-100 rounded-lg p-3 mb-3 bg-slate-50 space-y-2">
              <input placeholder="Name (e.g. Vacation)" value={typeForm.name}
                onChange={e => setTypeForm(f => ({...f, name: e.target.value}))}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs"
                data-testid="leave-type-name" />
              <div className="flex gap-2">
                <input type="number" placeholder="Annual days" value={typeForm.annual_days}
                  onChange={e => setTypeForm(f => ({...f, annual_days: parseFloat(e.target.value)}))}
                  className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
                  data-testid="leave-type-days" />
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  <input type="checkbox" checked={typeForm.paid}
                    onChange={e => setTypeForm(f => ({...f, paid: e.target.checked}))}
                    data-testid="leave-type-paid" />
                  Paid
                </label>
              </div>
              <button onClick={handleAddType} disabled={saving}
                className="w-full px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
          {types.length === 0 ? (
            <p className="text-xs text-slate-400">No leave types yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {types.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2" data-testid={`leave-type-row-${t.id}`}>
                  <div>
                    <p className="text-sm text-slate-700">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.annual_days} days/yr · {t.paid ? 'Paid' : 'Unpaid'}</p>
                  </div>
                  <button onClick={() => handleDeleteType(t.id)} className="p-1.5 text-slate-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Leave Requests */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex justify-between items-center mb-3">
          <p className="text-sm font-semibold text-slate-800">
            {canManage ? 'Leave Requests' : 'My Requests'}
          </p>
          {types.length > 0 && (
            <button onClick={() => setShowRequest(s => !s)} data-testid="request-leave-btn"
              className="flex items-center gap-1 text-xs font-medium text-orange-500 hover:text-orange-600">
              <Plus size={13} /> Request Leave
            </button>
          )}
        </div>

        {showRequest && (
          <div className="border border-slate-100 rounded-lg p-3 mb-3 bg-slate-50 space-y-2">
            <select value={reqForm.leave_type_id}
              onChange={e => setReqForm(f => ({...f, leave_type_id: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              data-testid="request-leave-type">
              <option value="">Select leave type</option>
              {types.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <input type="date" value={reqForm.start_date}
                onChange={e => setReqForm(f => ({...f, start_date: e.target.value}))}
                className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
                data-testid="request-start-date" />
              <input type="date" value={reqForm.end_date}
                onChange={e => setReqForm(f => ({...f, end_date: e.target.value}))}
                className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
                data-testid="request-end-date" />
            </div>
            <input type="number" min="0.5" step="0.5" value={reqForm.days}
              onChange={e => setReqForm(f => ({...f, days: parseFloat(e.target.value)}))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs"
              placeholder="Number of days"
              data-testid="request-days" />
            <button onClick={handleRequest} disabled={saving}
              className="w-full px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        )}

        {requests.length === 0 ? (
          <p className="text-xs text-slate-400">No leave requests.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {requests.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2.5" data-testid={`leave-req-${r.id}`}>
                <div className="min-w-0">
                  {canManage && <p className="text-xs font-medium text-slate-700">{r.employee_name}</p>}
                  <p className="text-xs text-slate-500">
                    {r.leave_type_name} · {r.start_date} → {r.end_date} · {r.days}d
                  </p>
                  {r.paid_amount != null && (
                    <p className="text-xs text-teal-600 font-medium">${r.paid_amount?.toFixed(2)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[r.status] || ''}`}>
                    {r.status}
                  </span>
                  {canManage && r.status === 'pending' && (
                    <>
                      <button onClick={() => handleApprove(r.id)} data-testid={`approve-leave-${r.id}`}
                        className="p-1 text-teal-600 hover:bg-teal-50 rounded"><Check size={14} /></button>
                      <button onClick={() => handleReject(r.id)} data-testid={`reject-leave-${r.id}`}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
